// Iter 646 — Scan Cepat ACC (Adit): SCAN → PERIKSA → ACC → otomatis siap scan berikutnya.
//   Scan TIDAK pernah memberi ACC. ACC memakai endpoint resmi yang sudah ada (idempoten di backend).
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, CheckCircle2, AlertTriangle, RefreshCw, ScanLine, XCircle } from "lucide-react";
import ScanMockupModal, { playScanBeep } from "./scan_mockup";
import { channelMismatch, openChannelFor, takePendingScan, ChannelMismatchNotice } from "./channel";
import { playTone, TONE_LEGEND } from "./scan_tones";
import { AiDetails, ConfirmCut, initialVal, verifiedBody } from "./acc_ai_details";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const MISMATCH = [["ukuran", "Ukuran"], ["warna", "Warna"], ["jumlah", "Jumlah"], ["produk", "Produk/Desain"], ["lainnya", "Lainnya"]];
const opt = { withCredentials: true, timeout: 20000 };
export const errText = (e) => e?.code === "ECONNABORTED" || !e?.response
  ? "Jaringan bermasalah / server tidak merespons. Status belum pasti — tekan Coba Lagi (tidak menggandakan proses)."
  : (e.response.data?.detail || `Gagal (${e.response.status})`);

const pendingOf = (d) => !d ? [] : d.source === "shopee" ? d.jobs.filter(j => j.eligible) : d.items.filter(i => i.eligible);
const allOf = (d) => !d ? [] : d.source === "shopee" ? d.jobs : d.items;

// Scanner keyboard-wedge global: hanya rangkaian ketikan sangat cepat + Enter.
// opts.ownRef: kolom scan sendiri (ditangani form). opts.anyInput: scan cepat yg "nyasar" ke kolom lain (mis. Cari) tetap diarahkan
// ke scan & teks nyasar dikembalikan. opts.tab: Tab sebagai akhiran scanner. Batas waktu longgar utk scanner Bluetooth.
const setNative = (el, v) => { const d = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value"); d?.set?.call(el, v); el.dispatchEvent(new Event("input", { bubbles: true })); };
export function useWedge(onCode, opts = {}) {
  const buf = useRef({ s: "", t: 0, t0: 0, el: null, v0: "" });
  const { ownRef, anyInput = false, tab = false, gap = 80, avg = 50 } = opts;
  useEffect(() => {
    const h = (e) => {
      const el = e.target;
      const editable = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
      if (editable && (!anyInput || el === ownRef?.current || el.tagName === "SELECT" || el.type === "checkbox" || el.type === "radio" || el.closest?.('[role="dialog"]'))) return;
      const b = buf.current, now = Date.now();
      if (now - b.t > gap) { b.s = ""; b.t0 = now; b.el = editable ? el : null; b.v0 = editable ? String(el.value ?? "") : ""; }
      b.t = now;
      if (e.key === "Enter" || (tab && e.key === "Tab")) {
        const n = b.s.length;
        if (n >= 6 && (now - b.t0) / n < avg) {
          e.preventDefault(); e.stopPropagation();
          if (b.el && b.el.value !== undefined) setNative(b.el, b.v0);
          onCode(b.s);
        }
        b.s = "";
      } else if (e.key.length === 1) b.s += e.key;
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onCode, ownRef, anyInput, tab, gap, avg]);
}

export function AccFastScan({ onOfflineChanged }) {
  const inputRef = useRef(null);
  const WEDGE = useRef({ ownRef: inputRef, anyInput: true, tab: true, gap: 200, avg: 60 }).current;
  const burst = useRef({ t0: 0, t: 0, n: 0 });
  const idle = useRef(null);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | error | channel
  const [mm, setMm] = useState(null);
  const [err, setErr] = useState("");
  const [scanRec, setScanRec] = useState(""); // "" | ok | fail
  const [acc, setAcc] = useState(null); // {state: busy|ok|fail, msg, fwd:[]}
  const [unrecOk, setUnrecOk] = useState(false);
  const [mis, setMis] = useState(null); // null | "pick" | {label}
  const [cam, setCam] = useState(false);
  const fromCam = useRef(false);
  const last = useRef({ code: "", at: 0 });
  const accLock = useRef(false);
  const reqId = useRef(0);
  const readyAt = useRef(0);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState({}); // rincian barang per unit ACC (hasil AI / perbaikan Adit)

  const focus = () => setTimeout(() => inputRef.current?.focus(), 30);

  const lookup = useCallback(async (raw, opts = {}) => {
    const c = String(raw || "").replace(/\s+/g, "").toUpperCase();
    if (!c) return;
    const now = Date.now();
    if (!opts.silent && last.current.code === c && now - last.current.at < 1500) return;
    last.current = { code: c, at: now };
    const id = ++reqId.current;
    if (!opts.silent) {
      setPhase("loading"); setErr(""); setAcc(null); setUnrecOk(false); setMis(null); setScanRec(""); setCode(""); setNote(""); readyAt.current = 0;
      axios.post(`${API}/orders/${encodeURIComponent(c)}/barcode-scan`, { device: "acc-fast" }, opt)
        .then(() => id === reqId.current && setScanRec("ok")).catch(() => id === reqId.current && setScanRec("fail"));
    }
    try {
      const r = await axios.get(`${API}/warehouse/acc-scan/${encodeURIComponent(c)}`, opt);
      if (id !== reqId.current) return;
      setData(r.data); setPhase("ready");
      if (!opts.silent) setLines(Object.fromEntries(Object.entries(r.data.ai_details || {}).map(([k, d]) => [k, initialVal(d)])));
      if (!opts.silent) {
        readyAt.current = Date.now();
        const all = allOf(r.data);
        playTone(all.length > 0 && all.every(i => i.acc_at) ? "done" : pendingOf(r.data).length ? "ready" : "blocked");
      }
    } catch (e) {
      if (id !== reqId.current) return;
      const mm = channelMismatch(e);
      setMm(mm ? { ...mm, code: c } : null);
      setData(null); setPhase(mm ? "channel" : "error"); setErr(mm ? "" : errText(e)); last.current = { code: "", at: 0 };
      playScanBeep({ kind: "err", durationMs: 180 });
    }
    focus();
  }, []);
  useWedge(lookup, WEDGE);
  useEffect(() => { focus(); const p = takePendingScan("acc"); if (p) lookup(p); }, [lookup]);

  const doAcc = async () => {
    if (accLock.current || !data) return;
    const todo = pendingOf(data);
    if (!todo.length) return;
    if (data.source === "shopee" && todo.some(j => j.needs_unrecorded_confirm) && !unrecOk) {
      setAcc({ state: "fail", msg: "Centang konfirmasi pemeriksaan fisik untuk pekerjaan dengan Status Cetak Belum Tercatat." });
      return;
    }
    accLock.current = true;
    setAcc({ state: "busy", msg: "Menyimpan ACC…" });
    const fwd = [], cuts = [];
    try {
      for (const it of todo) {
        if (data.source === "shopee") {
          const vb = verifiedBody(data.ai_details?.[it.key], lines[it.key]);
          const body = { ...(it.needs_unrecorded_confirm ? { confirm_unrecorded_print: true, physical_checked: true } : {}), ...vb };
          const r = await axios.post(`${API}/shopee/${it.key}/acc-goods`, Object.keys(body).length ? body : null, opt);
          fwd.push({ key: it.key, title: it.title || it.process, f: r.data?.bordir_forward });
          cuts.push({ key: it.key, title: it.title || it.process, c: r.data?.acc_stock_cut });
        } else {
          const r = await axios.post(`${API}/orders/${encodeURIComponent(data.order_number)}/products/${it.key}/acc-ready`,
            verifiedBody(data.ai_details?.[it.key], lines[it.key]), opt);
          cuts.push({ key: it.key, title: it.title || it.name, c: r.data?.acc_stock_cut });
        }
      }
      setAcc({ state: "ok", msg: `ACC Berhasil · ${todo.length} item`, fwd, cuts });
      playTone("success");
      try { window.navigator?.vibrate?.(80); } catch { /* noop */ }
      window.dispatchEvent(new Event("acc-fast-done"));
      if (data.source === "offline") Promise.resolve(onOfflineChanged?.()).catch(() => {});
      await lookup(data.order_number, { silent: true });
      setAcc(a => ({ ...a, state: "ok" }));
      if (fromCam.current) setTimeout(() => setCam(true), 900);
    } catch (e) {
      setAcc({ state: "fail", msg: errText(e), retry: true });
      playScanBeep({ kind: "err", durationMs: 180 });
      lookup(data.order_number, { silent: true });
    } finally { accLock.current = false; focus(); }
  };

  // Enter di kolom kosong = tombol ACC. Sama dengan Scan Cepat Shella (aman dari Enter ganda scanner).
  const enterConfirm = () => {
    if (phase !== "ready" || !data) { if (phase === "loading") setNote("Data masih dimuat — tunggu sebentar."); return; }
    if (!readyAt.current || Date.now() - readyAt.current < 600) return;
    if (mis === "pick" || cam || document.querySelector('[role="dialog"],[aria-modal="true"]')) return;
    if (accLock.current || acc?.state === "busy") return;
    if (mis) { setNote("Barang dilaporkan tidak sesuai — Enter dinonaktifkan. Klik tombol hijau bila tetap ingin ACC."); playTone("blocked"); return; }
    const todo = pendingOf(data);
    if (acc?.state === "ok" || (!todo.length && allOf(data).length > 0 && allOf(data).every(i => i.acc_at))) { setNote("Barang sudah di-ACC — tidak dicatat ulang. Silakan scan barcode berikutnya."); playTone("done"); return; }
    if (!todo.length) { setNote("Belum bisa ACC — lihat status di bawah. Enter tidak meng-ACC barang ini."); playTone("blocked"); return; }
    setNote(""); doAcc();
  };

  // Nilai DOM langsung (bukan state yg bisa tertinggal saat scanner sangat cepat).
  const submitBox = () => {
    clearTimeout(idle.current); fromCam.current = false;
    const v = String(inputRef.current?.value ?? code);
    if (v.trim()) lookup(v); else enterConfirm();
  };
  // Scanner berakhiran Tab, atau tanpa akhiran: rangkaian cepat ≥6 karakter lalu diam 300 ms → cari otomatis.
  const onBoxKey = (e) => {
    if (e.key === "Tab" && String(e.currentTarget.value).trim()) { e.preventDefault(); submitBox(); return; }
    if (e.key.length !== 1) return;
    const b = burst.current, now = Date.now();
    if (now - b.t > 200) { b.t0 = now; b.n = 0; }
    b.t = now; b.n += 1;
    clearTimeout(idle.current);
    idle.current = setTimeout(() => { const x = burst.current; if (x.n >= 6 && (x.t - x.t0) / (x.n - 1 || 1) < 60 && String(inputRef.current?.value || "").trim()) submitBox(); }, 300);
  };
  useEffect(() => () => clearTimeout(idle.current), []);

  const retryForward = async (jobId) => {
    try {
      const r = await axios.post(`${API}/shopee/${jobId}/bordir/retry-forward`, null, opt);
      setAcc(a => ({ ...a, fwd: (a?.fwd || []).map(x => x.key === jobId ? { ...x, f: r.data?.bordir_forward } : x) }));
    } catch (e) { setAcc(a => ({ ...a, msg: `${a?.msg || ""} · Coba lagi penerusan gagal: ${errText(e)}` })); }
    focus();
  };

  const sendMismatch = async (reason, label) => {
    try {
      await axios.post(`${API}/warehouse/acc-scan/${encodeURIComponent(data.order_number)}/mismatch`, { reason, source: data.source }, opt);
      setMis({ label }); playScanBeep({ freq: 600, durationMs: 140 });
      lookup(data.order_number, { silent: true });
    } catch (e) { setMis({ label: `Gagal mencatat: ${errText(e)}` }); }
    focus();
  };

  const items = allOf(data);
  const pending = pendingOf(data);
  const allDone = items.length > 0 && items.every(i => i.acc_at);
  return <section className="afs" data-testid="acc-fast-scan">
    <form className="afs-bar" onSubmit={(e) => { e.preventDefault(); submitBox(); }}>
      <ScanLine size={20} aria-hidden="true"/>
      <input ref={inputRef} value={code} onChange={e => setCode(e.target.value)} placeholder="Scan ACC: barcode nota / pesanan…"
        autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} enterKeyHint="search"
        onKeyDown={onBoxKey}
        onBlur={() => { if (!cam) setTimeout(() => { const a = document.activeElement; if (!a || a === document.body) inputRef.current?.focus(); }, 150); }}
        data-testid="acc-fast-input"/>
      <button type="button" className="afs-cam" onClick={() => setCam(true)} aria-label="Scan ACC pakai kamera" data-testid="acc-fast-camera"><Camera size={18}/></button>
    </form>
    <div className="afs-hint">Scan barcode → periksa barang → tekan <kbd>Enter</kbd> untuk ACC. Scan tidak langsung meng-ACC.<br/><small data-testid="acc-fast-tone-legend">{TONE_LEGEND}</small></div>
    {note && <div className="afs-msg afs-warn" role="status" data-testid="acc-fast-note">{note}</div>}

    {phase === "loading" && <div className="afs-msg" data-testid="acc-fast-loading"><RefreshCw size={14} className="spin"/> Membaca data…</div>}
    {phase === "error" && <div className="afs-msg afs-err" role="alert" data-testid="acc-fast-error"><XCircle size={16}/> {err}</div>}
    {phase === "channel" && mm && <ChannelMismatchNotice m={mm} testid="acc-fast-channel" onOpen={() => openChannelFor(["gudang"], mm.channel, "acc", mm.code)}/>}

    {phase === "ready" && data && <div className={`afs-panel${allDone ? " done" : ""}`} data-testid="acc-fast-panel">
      <div className="afs-head">
        <div>
          <span className={`afs-src afs-src-${data.source}`}>{data.source === "shopee" ? "SHOPEE" : "OFFLINE"}</span>
          <strong className="afs-num" data-testid="acc-fast-order">{data.order_number}</strong>
          <span className="afs-cust">{data.customer}</span>
        </div>
        <span className={`afs-state ${allDone ? "ok" : pending.length ? "wait" : "block"}`} data-testid="acc-fast-state">
          {allDone ? "Sudah Di-ACC" : pending.length ? "Siap Diperiksa" : "Belum Bisa ACC"}</span>
      </div>
      <div className="afs-steps" data-testid="acc-fast-steps">
        <span className={scanRec}>{scanRec === "fail" ? "Scan Tidak Tercatat" : "Scan Tercatat"}</span>
        <span className={allDone || acc?.state === "ok" ? "ok" : ""}>ACC Berhasil</span>
        <span className={forwardedClass(data, acc)}>Berhasil Diteruskan</span>
      </div>
      <Body data={data}/>
      {allOf(data).map(it => {
        const det = data.ai_details?.[it.key];
        const pending = !!it.acc_at && det?.cut?.status === "unverified";  // ACC sudah, stok belum dipotong → boleh Konfirmasi Rincian
        const cutKey = data.source === "shopee" ? `acc-cut:marketplace:${it.key}` : `acc-cut:offline:${data.order_number}:${it.key}`;
        return <React.Fragment key={it.key}>
          <AiDetails unitKey={it.key} title={allOf(data).length > 1 ? (it.title || it.name || it.process) : ""}
            det={det} val={lines[it.key]} locked={(!!it.acc_at && !pending) || acc?.state === "busy"}
            onChange={v => setLines(m => ({ ...m, [it.key]: v }))}/>
          {pending && det?.file_id && <ConfirmCut unitKey={it.key} cutKey={cutKey} det={det} val={lines[it.key]}
            post={async b => (await axios.post(`${API}/acc-stock-cut/confirm`, b, opt)).data}
            onDone={() => lookup(data.order_number, { silent: true })}/>}
        </React.Fragment>;
      })}
      {data.mismatch_reports?.length > 0 && <div className="afs-msg afs-warn" data-testid="acc-fast-mismatch-history">
        <AlertTriangle size={14}/> Pernah dilaporkan tidak sesuai: {data.mismatch_reports.map(m => `${m.reason_label} (${m.actor_name}, ${fmt(m.at)})`).join(" · ")}</div>}
      {data.source === "shopee" && pending.some(j => j.needs_unrecorded_confirm) && <label className="afs-confirm" data-testid="acc-fast-unrec">
        <input type="checkbox" checked={unrecOk} onChange={e => { setUnrecOk(e.target.checked); focus(); }} data-testid="acc-fast-unrec-check"/>
        Status cetak belum tercatat — saya sudah memeriksa BARANG FISIK sesuai mockup aktif.</label>}
      {acc && <div className={`afs-msg afs-${acc.state}`} role="status" data-testid="acc-fast-result">
        {acc.state === "ok" ? <CheckCircle2 size={16}/> : acc.state === "busy" ? <RefreshCw size={14} className="spin"/> : <XCircle size={16}/>} {acc.msg}
        {acc.state === "fail" && acc.retry && <button type="button" className="afs-retry" onClick={doAcc} data-testid="acc-fast-retry">Coba Lagi</button>}
        {(acc.fwd || []).map(x => <div key={x.key} className="afs-fwd" data-testid={`acc-fast-fwd-${x.key}`}>{fwdText(x)}
          {x.f?.state === "failed" && <button type="button" className="afs-retry" onClick={() => retryForward(x.key)} data-testid={`acc-fast-fwd-retry-${x.key}`}>Coba Lagi Penerusan</button>}</div>)}
        {(acc.cuts || []).filter(x => cutText(x)).map(x => <div key={`cut-${x.key}`} className="afs-fwd" style={{ color: CUT_OK.has(x.c.status) ? "#15803d" : "#b45309", fontWeight: 600 }}
          data-testid={`acc-fast-stock-cut-${x.key}`}>{cutText(x)}</div>)}
      </div>}
      {mis && mis !== "pick" && <div className="afs-msg afs-warn" data-testid="acc-fast-mismatch-done">Tercatat: Barang Tidak Sesuai — {mis.label}. Tidak di-ACC.</div>}
      {!allDone && <div className="afs-actions">
        <button type="button" className="afs-acc" disabled={!pending.length || acc?.state === "busy"} onClick={doAcc} data-testid="acc-fast-acc-btn">
          {acc?.state === "busy" ? "Menyimpan…" : <>{`ACC — BARANG SESUAI${pending.length > 1 ? ` (${pending.length})` : ""}`}{pending.length > 0 && <kbd className="afs-kbd" data-testid="acc-fast-enter-hint">Enter</kbd>}</>}</button>
        <button type="button" className="afs-bad" onClick={() => setMis(mis === "pick" ? null : "pick")} disabled={acc?.state === "busy"} data-testid="acc-fast-mismatch-btn">Barang Tidak Sesuai</button>
      </div>}
      {mis === "pick" && <div className="afs-mis" data-testid="acc-fast-mismatch-pick">
        {MISMATCH.map(([k, l]) => <button key={k} type="button" onClick={() => sendMismatch(k, l)} data-testid={`acc-fast-mismatch-${k}`}>{l}</button>)}
      </div>}
    </div>}

    <ScanMockupModal open={cam} onClose={() => { setCam(false); focus(); }} searchOnly title="Scan ACC"
      onOpenOrder={(c) => { fromCam.current = true; setCam(false); lookup(c); }}/>
  </section>;
}

export function fmt(iso) {
  try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
}

function forwardedClass(d, acc) {
  if ((acc?.fwd || []).some(x => x.f?.state === "failed")) return "fail";
  const all = allOf(d);
  if (!all.length || !all.every(i => i.acc_at)) return "";
  if (d.source === "shopee") return all.every(j => j.warehouse_state === "completed" || j.bordir_forward?.state === "forwarded") ? "ok" : "";
  return all.every(i => i.forwarded) ? "ok" : "";
}

const CUT_OK = new Set(["cut", "manual_done"]);
export function cutText(x) {
  const c = x.c;
  if (!c || !c.status || c.status === "off") return "";
  return `${x.title || "Barang"}: ${c.message || c.label || ""}`;
}

function fwdText(x) {
  const s = x.f?.state;
  if (s === "forwarded") return `${x.title || "Pekerjaan"}: Berhasil Diteruskan ke Desain Bordir`;
  if (s === "failed") return `${x.title || "Pekerjaan"}: ACC tersimpan, GAGAL diteruskan — ${x.f.reason || "alasan tidak diketahui"}`;
  return `${x.title || "Pekerjaan"}: diteruskan sesuai routing produksi`;
}

function Body({ data }) {
  if (data.source === "offline") {
    const total = data.items.reduce((n, i) => n + (i.qty || 0), 0);
    return <div className="afs-body">
      <img className="afs-thumb" src={`${BASE}${data.thumb}`} alt="Mockup" loading="eager" onError={e => { e.currentTarget.style.visibility = "hidden"; }}/>
      <div className="afs-items">
        <div className="afs-total" data-testid="acc-fast-total">Total harus ada: <b>{total} pcs</b></div>
        {data.items.length === 0 && <div className="afs-msg afs-warn">Tidak ada produk nota ini di tahap Gudang.</div>}
        {data.items.map(i => <div key={i.key} className="afs-item" data-testid={`acc-fast-item-${i.key}`}>
          <div className="afs-item-name">{i.name}{i.material ? ` · ${i.material}` : ""}{i.acc_at ? <em className="afs-ok"> Sudah Di-ACC ({i.acc_by})</em> : null}</div>
          <div className="afs-sizes">{(i.sizes.length ? i.sizes : [{ size: "—", color: i.color || "—", qty: i.qty }]).map((s, k) =>
            <span key={k} className="afs-size"><b>{s.size}</b> {s.color !== "—" ? s.color : ""}{s.variant ? ` · ${s.variant}` : ""} <strong>{s.qty} pcs</strong></span>)}</div>
          {i.reasons.length > 0 && <div className="afs-reason">Belum bisa ACC: {i.reasons.join("; ")}</div>}
        </div>)}
        {data.other_products > 0 && <div className="afs-note">{data.other_products} produk lain di nota ini belum/tidak di tahap Gudang.</div>}
      </div>
    </div>;
  }
  const total = data.products.reduce((n, p) => n + (p.qty || 0), 0);
  return <div className="afs-body">
    <div className="afs-thumbs">{data.jobs.map(j => j.thumb
      ? <img key={j.key} className="afs-thumb" src={`${BASE}${j.thumb}`} alt={j.title || "Mockup"} loading="eager"/>
      : <div key={j.key} className="afs-thumb afs-thumb-empty">Mockup tidak tersedia</div>)}</div>
    <div className="afs-items">
      {data.products_available
        ? <><div className="afs-total" data-testid="acc-fast-total">Total harus ada: <b>{total} pcs</b></div>
          {data.products.map((p, k) => <div key={k} className="afs-item">
            <div className="afs-item-name">{p.name}</div>
            <div className="afs-sizes"><span className="afs-size"><b>{p.size}</b> {p.color !== "—" ? p.color : ""} <strong>{p.qty} pcs</strong></span></div>
          </div>)}</>
        : <div className="afs-msg afs-warn">Rincian produk Shopee belum tersinkron — periksa sesuai mockup.</div>}
      {data.jobs.map(j => <div key={j.key} className="afs-job" data-testid={`acc-fast-job-${j.key}`}>
        {j.process}{j.title ? ` · ${j.title}` : ""} — {j.acc_at ? <em className="afs-ok">Sudah Di-ACC ({j.acc_by})</em> : j.eligible ? "menunggu ACC" : <span className="afs-reason">Belum bisa ACC: {j.reasons.join("; ")}</span>}
      </div>)}
    </div>
  </div>;
}
